import React, { useMemo } from 'react';
import { useBox } from '@react-three/cannon';
import { useTexture } from '@react-three/drei';
import * as THREE from 'three';

import { ConnaughtBuilding } from './ConnaughtBuilding';
import { Autorickshaw, Cow, DTCBus, Sedan, Pedestrian, StreetDog } from './Entities';

const INNER_RADIUS = 30;
const ROAD_WIDTH = 12;
const MIDDLE_RADIUS = INNER_RADIUS + ROAD_WIDTH + 20;

export default function Delhi() {
  // Load Poly Haven Textures
  const [grassDiff, grassRough, asphaltDiff, asphaltNor, asphaltRough, wallDiff, wallRough] = useTexture([
    new URL('../assets/textures/grass_diff.jpg', import.meta.url).href,
    new URL('../assets/textures/grass_rough.jpg', import.meta.url).href,
    new URL('../assets/textures/asphalt_diff.jpg', import.meta.url).href,
    new URL('../assets/textures/asphalt_nor.jpg', import.meta.url).href,
    new URL('../assets/textures/asphalt_rough.jpg', import.meta.url).href,
    new URL('../assets/textures/wall_diff.jpg', import.meta.url).href,
    new URL('../assets/textures/wall_rough.jpg', import.meta.url).href,
  ]);
  
  useMemo(() => {
    [grassDiff, grassRough, asphaltDiff, asphaltNor, asphaltRough, wallDiff, wallRough].forEach(t => {
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
    });
    grassDiff.repeat.set(15, 15);
    grassRough.repeat.set(15, 15);
    asphaltDiff.repeat.set(20, 4); // Ring mapping can be weird, but simple repeat works ok
    asphaltNor.repeat.set(20, 4);
    asphaltRough.repeat.set(20, 4);
    wallDiff.repeat.set(4, 4);
    wallRough.repeat.set(4, 4);
  }, [grassDiff, grassRough, asphaltDiff, asphaltNor, asphaltRough, wallDiff, wallRough]);

  // Ground physics
  const [groundRef] = useBox(() => ({
    type: 'Static',
    position: [0, -0.5, 0],
    args: [200, 1, 200],
  }));

  // Generate radial layout data
  const { buildings, vehicles, pedestrians, dogs } = useMemo(() => {
    const b = [];
    const v = [];
    const p = [];
    const d = [];

    // Inner Circle Buildings
    const numInnerBuildings = 12;
    for (let i = 0; i < numInnerBuildings; i++) {
      const angle = (i / numInnerBuildings) * Math.PI * 2;
      if (i % 3 === 0) continue;
      
      const radius = INNER_RADIUS + ROAD_WIDTH / 2 + 10;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      
      // -angle + Math.PI / 2 made the buildings face outward. We subtract Math.PI to make them face inward.
      const rotY = -angle - Math.PI / 2;
      b.push({ position: [x, 0, z], rotation: [0, rotY, 0], width: 25, depth: 15 });
      
      for(let k = 0; k < 3; k++) {
        const isShopper = k === 0; // One out of three is a serious shopper walking into the store
        const pAngle = angle + (Math.random() - 0.5) * 0.2;
        
        // Base sidewalk radius
        const sidewalkRadius = 39.5;
        // Inside showroom radius (closer to the building core which is at r=46)
        const showroomRadius = 43.5;
        
        const wpts = [];
        const startPos = [Math.cos(pAngle) * sidewalkRadius, 0, Math.sin(pAngle) * sidewalkRadius];
        wpts.push(startPos);
        
        if (isShopper) {
          // Walk to the center of the building (where the showroom is)
          wpts.push([Math.cos(angle) * sidewalkRadius, 0, Math.sin(angle) * sidewalkRadius]);
          // Walk INTO the showroom
          wpts.push([Math.cos(angle) * showroomRadius, 0, Math.sin(angle) * showroomRadius]);
          // Browse inside (linger/wiggle slightly)
          wpts.push([Math.cos(angle + 0.01) * showroomRadius, 0, Math.sin(angle + 0.01) * showroomRadius]);
          // Walk back out to the sidewalk
          wpts.push([Math.cos(angle) * sidewalkRadius, 0, Math.sin(angle) * sidewalkRadius]);
        }
        
        // Continue walking down the street
        const nextAngle = angle + (Math.random() > 0.5 ? 0.3 : -0.3);
        wpts.push([Math.cos(nextAngle) * sidewalkRadius, 0, Math.sin(nextAngle) * sidewalkRadius]);

        p.push({
          pos: startPos,
          waypoints: wpts,
          color: ['#ff5722', '#3f51b5', '#e91e63', '#4caf50', '#ffc107', '#9c27b0'][Math.floor(Math.random()*6)],
          gender: Math.random() > 0.5 ? 'male' : 'female',
          hasShoppingBag: isShopper || Math.random() > 0.7
        });
      }
    }

    // Vehicles
    v.push({ type: 'bus', pos: [30, 0, 0], direction: 1, color: '#d32f2f' }); // Red DTC
    v.push({ type: 'bus', pos: [-30, 0, 0], direction: 1, color: '#388e3c' }); // Green DTC
    v.push({ type: 'auto', pos: [0, 0, 30], direction: -1 });
    v.push({ type: 'auto', pos: [0, 0, -30], direction: -1 });
    v.push({ type: 'sedan', pos: [21.21, 0, 21.21], direction: 1, color: '#f5f5f5' }); // White Taxi
    v.push({ type: 'sedan', pos: [-21.21, 0, -21.21], direction: 1, color: '#111' }); // Black car

    // Dogs in central park
    d.push([5, 0, 5]);
    d.push([-8, 0, 10]);
    d.push([0, 0, -12]);

    return { buildings: b, vehicles: v, pedestrians: p, dogs: d };
  }, []);

  return (
    <group>
      {/* Ground plane */}
      <mesh ref={groundRef} receiveShadow>
        <boxGeometry args={[200, 1, 200]} />
        <meshStandardMaterial color="#333333" roughness={1} />
      </mesh>

      {/* Central Park */}
      <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <circleGeometry args={[INNER_RADIUS - ROAD_WIDTH / 2 - 2, 64]} />
        <meshStandardMaterial map={grassDiff} roughnessMap={grassRough} color="#9ccc65" />
      </mesh>
      
      {/* Central Park Low Boundary Wall */}
      <mesh position={[0, 0.5, 0]} rotation={[-Math.PI / 2, 0, 0]} castShadow receiveShadow>
        <ringGeometry args={[INNER_RADIUS - ROAD_WIDTH / 2 - 2.5, INNER_RADIUS - ROAD_WIDTH / 2 - 2, 64]} />
        <meshStandardMaterial color="#EAE0CC" roughness={0.9} />
      </mesh>

      {/* Inner Circle Road */}
      <mesh position={[0, 0.06, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <ringGeometry args={[INNER_RADIUS - ROAD_WIDTH / 2 - 2, INNER_RADIUS + ROAD_WIDTH / 2, 64]} />
        <meshStandardMaterial map={asphaltDiff} normalMap={asphaltNor} roughnessMap={asphaltRough} color="#666" />
      </mesh>
      
      {/* Wide Paved Pedestrian Plaza in front of Buildings */}
      <mesh position={[0, 0.07, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <ringGeometry args={[INNER_RADIUS + ROAD_WIDTH / 2, INNER_RADIUS + ROAD_WIDTH / 2 + 10, 64]} />
        <meshStandardMaterial map={wallDiff} roughnessMap={wallRough} color="#b0a89e" />
      </mesh>

      {/* Render Buildings */}
      {buildings.map((b, i) => (
        <ConnaughtBuilding key={`cp-${i}`} buildingIndex={i} position={b.position} rotation={b.rotation} width={b.width} depth={b.depth} textures={{diff: wallDiff, rough: wallRough}} />
      ))}

      {/* Render Vehicles */}
      {vehicles.map((v, i) => {
        if (v.type === 'bus') return <DTCBus key={`v-${i}`} initialPosition={v.pos} radius={INNER_RADIUS} direction={v.direction} color={v.color} speed={10} />;
        if (v.type === 'auto') return <Autorickshaw key={`v-${i}`} initialPosition={v.pos} radius={INNER_RADIUS} direction={v.direction} speed={14} />;
        if (v.type === 'sedan') return <Sedan key={`v-${i}`} initialPosition={v.pos} radius={INNER_RADIUS} direction={v.direction} color={v.color} speed={16} />;
        return null;
      })}

      {/* Render Pedestrians */}
      {pedestrians.map((p, i) => (
        <Pedestrian key={`ped-${i}`} initialPosition={p.pos} waypoints={p.waypoints} color={p.color} gender={p.gender} hasShoppingBag={p.hasShoppingBag} />
      ))}

      {/* Render Dogs */}
      {dogs.map((d, i) => (
        <StreetDog key={`dog-${i}`} initialPosition={d} radius={15} />
      ))}
      
      {/* Some stray cows, this is Delhi after all */}
      <Cow initialPosition={[10, 0, -8]} radius={5} />
      <Cow initialPosition={[-12, 0, -2]} radius={5} />

    </group>
  );
};
